<template>
	<div>
		<VMenu
			v-if="isClickable && taskId"
			:delay="{ show: 200, hide: 0 }"
			:triggers="['hover']"
			:popper-hide-triggers="['hover']"
			placement="right-start"
			:skidding="-24"
			:distance="8"
			hide-group="task-priority"
		>
			<button
				v-tooltip="tooltip"
				type="button"
				class="task-priority"
				:class="classAddons"
				:aria-label="
					$t('task_priority', {
						priority: $t(priorityTranslations[priority]),
					})
				"
				@click="$emit('click')"
			>
				<span class="task-priority__icon--modify">
					<SuiIcon name="icon-pencil-filled-16" />
				</span>
				<span class="task-priority__icon--priority">
					<SuiIcon name="icon-task-filled-16" />
				</span>
				<span
					v-if="isRequiredFieldsMissing"
					class="task-priority__required-field-missing"
				>
					<span
						class="task-priority__required-field-missing-marker"
					/>
				</span>
			</button>

			<template #popper>
				<TaskTooltip :id="taskId" />
			</template>
		</VMenu>
		<div
			v-else
			class="task-priority"
			:class="classAddons"
			:aria-label="
				$t('task_priority', {
					priority: $t(priorityTranslations[priority]),
				})
			"
		>
			<SuiIcon
				class="task-priority__icon--priority"
				name="icon-task-filled-16"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables';
import { Priority, type Task } from '@/generated/graphql';
import { SuiIcon } from '@developers/scoro-ui';
import { computed, defineAsyncComponent } from 'vue';

const TaskTooltip = defineAsyncComponent(
	() => import('@/features/Task/components/TaskTooltip/TaskTooltip.vue')
);

export type TaskPriorityProps = {
	priority: Priority;
	taskId?: Task['id'];
	isDone?: boolean;
	isRequiredFieldsMissing?: Task['isRequiredFieldsMissing'];
	isClickable?: boolean;
};

const props = defineProps<TaskPriorityProps>();
defineEmits(['click']);

const { t } = useTranslation();

const priorityTranslations: Record<Priority, string> = {
	[Priority.High]: 'priority1',
	[Priority.Normal]: 'priority2',
	[Priority.Low]: 'priority3',
};

const classAddons = computed(() => ({
	[`task-priority--${props.priority.toLowerCase()}`]: !props.isDone,
	'task-priority--is-done': props.isDone,
	'task-priority--not-clickable': !props.isClickable,
}));

const tooltip = computed(() => {
	if (props.isRequiredFieldsMissing) {
		return t('task_mandatory_fields_missing');
	}

	return null;
});
</script>

<style lang="scss">
.task-priority {
	position: relative;
	padding: var(--task-priority-padding, 0.5rem);

	&--high {
		--sui-icon-fill: #{$brand-50};
	}

	&--normal {
		--sui-icon-fill: #{$yellow-50};
	}

	&--low {
		--sui-icon-fill: #{$blue-50};
	}

	&--is-done {
		--sui-icon-fill: #{$neutral-50};
	}

	&__icon--modify {
		display: none;
	}

	&__required-field-missing {
		display: flex;
		position: absolute;
		right: 0.625rem;
		bottom: 0.625rem;
	}

	&__required-field-missing-marker {
		height: 0.875rem;
		width: 0.875rem;
		display: inline-block;
		background: $orange-50;
		border-radius: 50%;
		border: 1px solid #{$neutral-00};
	}

	&:focus:not(&--not-clickable),
	&:hover:not(&--not-clickable) {
		.task-priority__icon--modify {
			display: block;
		}

		.task-priority__icon--priority {
			display: none;
		}
	}

	&--not-clickable {
		cursor: default;
	}
}
</style>
